import { apiClient } from './api';
import { safeArray } from '../utils/money';
import {
  Balance,
  MultiCurrencyBalance,
  CryptoConversion,
  Withdrawal,
  ExchangeRate,
  BankAccount
} from '../types';

/**
 * 把后端余额响应归一化为前端统一结构。
 * 后端实际返回币种->可用余额（分）的映射，如 { "USD": 941, "EUR": 0 }，
 * 也可能返回 { balances: [...] } 旧格式，这里都做兜底。
 */
export function normalizeBalance(raw: any): Balance {
  const balances: MultiCurrencyBalance[] = [];

  if (raw && Array.isArray(raw.balances)) {
    raw.balances.forEach((b: any) => balances.push(b));
  } else if (raw && typeof raw === 'object') {
    Object.keys(raw).forEach((key) => {
      const val = raw[key];
      // 仅处理形如 { USD: 941 } 的币种数值映射
      if (typeof val === 'number') {
        balances.push({
          currency: key,
          available_balance: val,
          pending_balance: 0,
          frozen_balance: 0,
          total_balance: val,
          last_updated: new Date().toISOString(),
        });
      }
    });
  }

  const primary = balances[0];
  return {
    balances,
    total_value_usd: balances.reduce((s, b) => s + (b.total_balance || 0), 0),
    last_updated: new Date().toISOString(),
    available_balance: primary?.available_balance || 0,
    pending_balance: primary?.pending_balance || 0,
    frozen_balance: primary?.frozen_balance || 0,
    total_balance: primary?.total_balance || 0,
    currency: primary?.currency || 'USD',
  };
}

export class BalanceService {
  // 获取多币种余额
  static async getBalance(): Promise<Balance> {
    const response = await apiClient.get('/merchant/balance');
    return normalizeBalance(response.data?.data ?? response.data);
  }

  // 获取特定币种余额
  static async getCurrencyBalance(currency: string): Promise<MultiCurrencyBalance> {
    const response = await apiClient.get('/merchant/balance', {
      params: { currency }
    });
    return response.data;
  }

  // 获取余额历史
  static async getBalanceHistory(
    currency?: string,
    page: number = 1,
    limit: number = 20
  ): Promise<{
    data: any[];
    pagination: any;
  }> {
    const response = await apiClient.get('/merchant/balance/history', {
      params: { currency, page, limit }
    });
    const env = response.data || {};
    return {
      data: safeArray(env.data),
      pagination: env.meta || env.pagination || null,
    };
  }

  // 获取余额汇总
  static async getBalanceSummary(): Promise<any> {
    const response = await apiClient.get('/merchant/balance/summary');
    return response.data;
  }

  /**
   * 获取风控等级与资金预留/冻结信息。
   * 后端契约：GET /merchant/risk-profile
   * 接口未就绪时（404 等）由调用方兜底为隐藏/空态，这里仅做数据归一化。
   */
  static async getRiskProfile(): Promise<any> {
    const response = await apiClient.get('/merchant/risk-profile');
    return response.data?.data ?? response.data;
  }

  // 获取实时汇率
  static async getExchangeRates(
    fromCurrency?: string,
    toCurrency?: string
  ): Promise<ExchangeRate[]> {
    const response = await apiClient.get('/merchant/treasury/exchange/rate', {
      params: { from_currency: fromCurrency, to_currency: toCurrency }
    });
    return response.data;
  }

  // 货币转换
  static async convertCrypto(data: {
    from_currency: string;
    to_currency: string;
    amount: number;
  }): Promise<CryptoConversion> {
    const response = await apiClient.post('/merchant/treasury/convert', data);
    return response.data;
  }

  // 获取财务流水（替代conversions）
  static async getTransactions(
    page: number = 1,
    limit: number = 20,
    filters?: any
  ): Promise<{
    data: any[];
    pagination: any;
  }> {
    const response = await apiClient.get('/merchant/treasury/transactions', {
      params: { page, limit, ...filters }
    });
    const env = response.data || {};
    return {
      data: safeArray(env.data),
      pagination: env.meta || env.pagination || null,
    };
  }

  // 获取交易详情
  static async getTransaction(id: string): Promise<any> {
    const response = await apiClient.get(`/merchant/treasury/transactions/${id}`);
    return response.data;
  }

  // 创建结算（替代提现）
  static async createSettlement(data: {
    amount: number;
    currency: string;
    destination_type: string;
    destination_id?: string;
    destination_details?: any;
  }): Promise<Withdrawal> {
    const response = await apiClient.post('/merchant/treasury/settlements', data);
    return response.data;
  }

  // 获取结算历史（替代提现历史）
  static async getSettlements(
    page: number = 1,
    limit: number = 20,
    status?: string
  ): Promise<{
    data: Withdrawal[];
    pagination: any;
  }> {
    const response = await apiClient.get('/merchant/treasury/settlements', {
      params: { page, limit, status }
    });
    const env = response.data || {};
    return {
      data: safeArray<Withdrawal>(env.data),
      pagination: env.meta || env.pagination || null,
    };
  }

  // 获取结算详情
  static async getSettlement(id: string): Promise<any> {
    const response = await apiClient.get(`/merchant/treasury/settlements/${id}`);
    return response.data;
  }

  // 取消结算
  static async cancelSettlement(id: string): Promise<void> {
    await apiClient.post(`/merchant/treasury/settlements/${id}/cancel`);
  }

  // 获取账户列表（treasury accounts）
  static async getAccounts(): Promise<any[]> {
    const response = await apiClient.get('/merchant/treasury/accounts');
    return safeArray(response.data?.data ?? response.data);
  }

  // 创建账户
  static async createAccount(data: {
    currency: string;
    type: string;
  }): Promise<any> {
    const response = await apiClient.post('/merchant/treasury/accounts', data);
    return response.data;
  }

  // 获取账户详情
  static async getAccount(id: string): Promise<any> {
    const response = await apiClient.get(`/merchant/treasury/accounts/${id}`);
    return response.data;
  }

  // 银行验证
  static async verifyBankAccount(data: any): Promise<any> {
    const response = await apiClient.post('/merchant/treasury/verification', data);
    return response.data;
  }

  // 获取银行验证状态
  static async getBankVerificationStatus(id: string): Promise<any> {
    const response = await apiClient.get(`/merchant/treasury/verification/${id}`);
    return response.data;
  }

  // 创建转账
  static async createTransfer(data: {
    from_account_id: string;
    to_account_id: string;
    amount: number;
    currency: string;
    description?: string;
  }): Promise<any> {
    const response = await apiClient.post('/merchant/treasury/transfers', data);
    return response.data;
  }

  // 获取转账列表
  static async getTransfers(
    page: number = 1,
    limit: number = 20,
    filters?: any
  ): Promise<{
    data: any[];
    pagination: any;
  }> {
    const response = await apiClient.get('/merchant/treasury/transfers', {
      params: { page, limit, ...filters }
    });
    return response.data;
  }

  // 取消转账
  static async cancelTransfer(id: string): Promise<void> {
    await apiClient.post(`/merchant/treasury/transfers/${id}/cancel`);
  }

  // ============= 兼容性别名（向后兼容） =============

  /**
   * @deprecated 使用 getSettlements 替代
   * 获取提现历史（兼容别名）
   */
  static async getWithdrawals(
    page: number = 1,
    limit: number = 20,
    status?: string
  ): Promise<{
    data: Withdrawal[];
    pagination: any;
  }> {
    return this.getSettlements(page, limit, status);
  }

  /**
   * @deprecated 使用 createSettlement 替代
   * 提现申请（兼容别名）
   */
  static async createWithdrawal(data: {
    currency: string;
    amount: number;
    destination_type: 'bank_account' | 'crypto_wallet';
    destination_id?: string;
    destination_address?: string;
    memo?: string;
  }): Promise<Withdrawal> {
    return this.createSettlement({
      amount: data.amount,
      currency: data.currency,
      destination_type: data.destination_type,
      destination_id: data.destination_id,
      destination_details: {
        address: data.destination_address,
        memo: data.memo,
      },
    });
  }

  /**
   * @deprecated 使用 cancelSettlement 替代
   * 取消提现（兼容别名）
   */
  static async cancelWithdrawal(id: string): Promise<void> {
    return this.cancelSettlement(id);
  }
} 